import { Card, CardContent } from "@/components/ui/card";
import { Hint } from "@/components/ui/hint";

export function StatCard({ title, value, subtitle, icon }: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm text-admin-text-subtle">{title}</p>
            <p className="text-2xl font-bold text-admin-text-primary">{value}</p>
            {subtitle && <p className="text-xs text-admin-text-subtle">{subtitle}</p>}
          </div>
          <div className="rounded-lg bg-admin-primary/10 p-2.5">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * The card label, dotted-underlined when it carries an explanation, so a
 * reader can see there is something to hover rather than having to try.
 * Same affordance as the B2B economics dashboard.
 */
export function StatLabel({ label, explain }: { label: string; explain?: string }) {
  return (
    <p className="text-xs text-admin-text-subtle">
      <Hint text={explain}>{label}</Hint>
    </p>
  );
}

export function CompactStat({ label, value, color, hint, explain }: {
  label: string;
  value: string;
  color?: string;
  /** The arithmetic behind the number, in small type under it. */
  hint?: string;
  /** What the number counts. On the dotted label, so it can be found. */
  explain?: string;
}) {
  return (
    <Card>
      <CardContent className="px-4 py-3">
        <StatLabel label={label} explain={explain} />
        <p className={`text-lg font-bold ${color ?? "text-admin-text-primary"}`}>{value}</p>
        {hint && <p className="text-xs text-admin-text-subtle mt-0.5">{hint}</p>}
      </CardContent>
    </Card>
  );
}

export function KpiCard({ label, count, amount, color, subtitle, explain }: {
  label: string;
  count: string;
  amount: string;
  color?: string;
  subtitle?: string;
  explain?: string;
}) {
  return (
    <Card>
      <CardContent className="px-4 py-3">
        <StatLabel label={label} explain={explain} />
        <p className={`text-lg font-bold ${color ?? "text-admin-text-primary"}`}>{count}</p>
        <p className={`text-sm ${color ?? "text-admin-text-secondary"}`}>{amount}</p>
        {subtitle && <p className="text-xs text-admin-text-subtle mt-0.5">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}
